import type { AxiosRequestConfig } from 'axios';
import { getHashedString } from '@/common/api/utils/utils';
import { cloneDeep, isPlainObject, omit } from 'lodash-es';
import { ERROR_TYPE, ErrorStateController } from '@/worker/commands/config/interceptors.types';

export const isPollingSuffix = '@isPolling';
export const useApiKeyOnlyFrameName = (frameName: string) => {
  return frameName.includes(encodeURIComponent(isPollingSuffix));
};
export const getFrameName = (frameName: string) => {
  return frameName.replace(encodeURIComponent(isPollingSuffix), '');
};

// 폴링 dedup 키 만료 시간(ms). 응답/에러/취소 없이 hang한 요청의 키가 영구 잔존하여
// 동일 프레임 폴링이 영구 freeze 되는 것을 방지하기 위한 안전장치. (DSP-43281)
const POLLING_DEDUP_TTL_MS = 60_000;

// key -> 만료 시각(ms). 만료 시각을 지나면 hang으로 간주하여 다음 폴링을 허용(자동 복구).
const apiCallMap = new Map<string, number>();

const getSortedObject = (obj: Record<string, any>) => {
  return Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
};

const EXCEPTION_PARAMS = ['fromTime', 'toTime', 'lastTime'];
const getExceptionParams = (params: any) => {
  if (Array.isArray(params)) {
    return params.map((item) => {
      return getExceptionParams(item);
    });
  }
  if (isPlainObject(params)) {
    return getSortedObject(omit(cloneDeep(params), EXCEPTION_PARAMS));
  }
  return params;
};

const getParsedData = (data: string) => {
  try {
    const parsedData = JSON.parse(data);
    return typeof parsedData === 'object' ? parsedData : data;
  } catch {
    return data;
  }
};

const getStringifyData = (data?: Record<string, any>) => {
  if (!data) {
    return data;
  }
  if (Array.isArray(data)) {
    return JSON.stringify([...data]);
  }
  return isPlainObject(data) ? JSON.stringify({ ...data }) : data;
};

const getKey = (config: AxiosRequestConfig, apiKey: string): string | null => {
  const { data, params, url } = config ?? {};
  if (!url) {
    return null;
  }

  const keyObject = useApiKeyOnlyFrameName(apiKey)
    ? { apiKey }
    : {
        url,
        data: getStringifyData(getExceptionParams(data)),
        params: getStringifyData(getExceptionParams(params)),
        apiKey,
      };
  return getHashedString(keyObject);
};

const TIME_RANGE_PARAMS = ['fromTime', 'toTime'];
const hasTimeRangeValue = (value: any): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => hasTimeRangeValue(item));
  }
  if (isPlainObject(value)) {
    const hasTimeRangeKey = TIME_RANGE_PARAMS.some((key) => {
      const timeValue = value[key];
      return timeValue !== null && timeValue !== undefined && timeValue !== '';
    });
    if (hasTimeRangeKey) {
      return true;
    }
    return Object.values(value).some((childValue) => hasTimeRangeValue(childValue));
  }
  return false;
};

/**
 * 요청 config의 params/data에 조정 가능한 시간 범위(fromTime/toTime) 값이
 * 하나라도 존재하는지 판별한다. (중첩 객체·배열 내부까지 재귀 탐색, 408 안내 문구 분기용)
 */
export const hasRequestTimeRange = (config?: AxiosRequestConfig): boolean => {
  const { data, params } = config ?? {};
  const parsedData = typeof data === 'string' ? getParsedData(data) : data;
  const parsedParams = typeof params === 'string' ? getParsedData(params) : params;
  return hasTimeRangeValue(parsedParams) || hasTimeRangeValue(parsedData);
};

const getResponseConfig = (config: AxiosRequestConfig) => {
  const { data, params, url } = config ?? {};
  let parsedData = getParsedData(data);
  let parsedParams = getParsedData(params);

  if (typeof parsedData === 'object') {
    parsedData = getExceptionParams(parsedData);
  }

  if (typeof parsedParams === 'object') {
    parsedParams = getExceptionParams(parsedParams);
  }

  return {
    url,
    data: parsedData,
    params: parsedParams,
  } as AxiosRequestConfig;
};

export const apiController = {
  isAvailableRequest: (config: AxiosRequestConfig, frameName: string = ''): boolean => {
    const key = getKey(config, frameName);
    if (!key) {
      return false;
    }
    const expireAt = apiCallMap.get(key);
    // 키 없음 → 요청 가능.
    if (expireAt === undefined) {
      return true;
    }
    // TTL 만료 → hang으로 간주하여 stale 키 정리 후 요청 허용(자동 복구).
    if (expireAt <= Date.now()) {
      apiCallMap.delete(key);
      return true;
    }
    // 유효한 in-flight 키 존재 → skip.
    return false;
  },
  request: (config: AxiosRequestConfig, frameName: string = '') => {
    const key = getKey(config, frameName);
    if (key) {
      apiCallMap.set(key, Date.now() + POLLING_DEDUP_TTL_MS);
    }
  },
  response: (config: AxiosRequestConfig, frameName: string = '') => {
    const key = getKey(getResponseConfig(config), frameName);
    if (key) {
      apiCallMap.delete(key);
    }
  },
};

const errorState: ErrorStateController = {
  LOCKED_USER: {
    isLogout: false,
  },
};
export const getErrorState = <T extends ERROR_TYPE>(type: T): ErrorStateController[T] => {
  return errorState[type];
};
